import URI from 'urijs';

import csrfToken from '@zimpel/common/csrf';
import {
  ApiError,
  AuthorizationError, BadRequestError, InternalServerError, NotFoundError,
  ValidationError,
} from './errors';

type Request<Body, Response> = (body: Body) => Promise<Response>;

type RequestMethod = 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type RequestQuery = Record<string, any>;
type RequestBody = Record<string, any>;

type RequestOptions<T> = {
  method: RequestMethod;
  url: string;
  query?: RequestQuery;
  body?: RequestBody;
  abort?: AbortConfig<T>;
}

type AbortConfig<T> = {
  id: string;
  value: T;
}

const request = async <ResponseData, >({
  method, url, query, body, abort,
}: RequestOptions<ResponseData>): Promise<ResponseData> => {
  try {
    const response = await window.fetch(
      fetchUrl(url, query),
      fetchOptions(method, abort, body),
    );

    if (response.ok) {
      notifyAppVersion(response.headers.get('App-Version'));
    }

    if (response.status === 204) {
      // We can't include undefined in the type signature without forcing every consumer to handle
      // it, even if the API will never return it, but we have to return something in the No Content
      // case. Callers need to know that their API can return 204 and then handle undefined, without
      // type check.
      return undefined as unknown as ResponseData;
    }
    if (response.status !== 200) {
      await handleError(response);
    }
    const contentType = response.headers.get('Content-Type');
    if (!contentType?.includes('application/json')) {
      throw Error(`Bad Content-Type ${contentType}`);
    }
    return response.json();
  } catch (error) {
    if (error.name === 'AbortError' && abort) {
      return abort.value;
    }
    throw error;
  }
};

type DownloadFileParams = {
  url: string,
  filename: string,
  body?: RequestBody,
}

const notifyAppVersion = (appVersion: string | null) => {
  const version = appVersion || 'unknown_version';
  const event = new CustomEvent('newAppVersion', { detail: version });
  window.dispatchEvent(event);
};

const downloadFile = async ({
  url, filename, body,
}: DownloadFileParams): Promise<void> => {
  const response = await window.fetch(
    fetchUrl(url),
    fetchOptions('POST', undefined, body),
  );

  if (response.ok) {
    notifyAppVersion(response.headers.get('App-Version'));
  }

  if (response.status !== 200) {
    await handleError(response);
  }
  const blob = await response.blob();
  // Currently there is no good programmatic browser compatible download.
  // You often see suggestions to do it like described in
  // https://medium.com/yellowcode/download-api-files-with-react-fetch-393e4dae0d9e
  const data = new Blob([blob]);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(data);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  if (link.parentNode) {
    link.parentNode.removeChild(link);
  }
};

const fetchUrl = (url: string, query?: RequestQuery): string => (
  URI(url).addQuery(query || {}).valueOf()
);

const fetchOptions = <ResponseData, >(
  method: string,
  abort?: AbortConfig<ResponseData>,
  body?: RequestBody,
): Partial<RequestInit> => {
  const result: Partial<RequestInit> = {
    credentials: 'include',
    headers: {
      /* eslint-disable @typescript-eslint/naming-convention */
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
      'X-Requested-With': 'XMLHttpRequest',
      /* eslint-enable @typescript-eslint/naming-convention */
    },
    method,
  };

  // Set abort signal for request and store the AbortController
  if (abort) {
    result.signal = ensureSingleRequest(abort.id);
  }

  // Body needs to be set explicitly only for requests that can have a body.
  // fetch() fails if the attribute is even set for GET or HEAD requests.
  if (body) {
    result.body = JSON.stringify(body);
  }

  return result;
};

const handleError = async <ResponseData, >(response: Response): Promise<ResponseData> => {
  // 400 Bad Request
  if (response.status === 400) {
    throw new BadRequestError();
  }

  // 403 Authorization Error
  if (response.status === 403) {
    throw new AuthorizationError();
  }

  // 404 Not Found
  if (response.status === 404) {
    throw new NotFoundError();
  }

  // 422 Unprocessable Entity
  if (response.status === 422) {
    const json = await response.json();
    throw new ValidationError(json.errors);
  }

  // 500 Internal Server Error
  if (response.status === 500) {
    throw new InternalServerError();
  }

  throw new ApiError(response.status, response.statusText);
};

const abortableRequests: Record<string, AbortController> = {};

const ensureSingleRequest = (abortId: string) => {
  if (abortableRequests[abortId]) {
    abortableRequests[abortId].abort();
  }
  const abortController = new AbortController();
  abortableRequests[abortId] = abortController;
  return abortController.signal;
};

const api = {
  request,
  downloadFile,
  get: <ResponseData, >(url: string, query?: RequestQuery): Promise<ResponseData> => request({ method: 'GET', url, query }),
  post: <ResponseData, >(url: string, body?: RequestBody): Promise<ResponseData> => request({ method: 'POST', url, body }),
  put: <ResponseData, >(url: string, body?: RequestBody): Promise<ResponseData> => request({ method: 'PUT', url, body }),
  patch: <ResponseData, >(url: string, body?: RequestBody): Promise<ResponseData> => request({ method: 'PATCH', url, body }),
  delete: <ResponseData, >(url: string, body?: RequestBody): Promise<ResponseData> => request({ method: 'DELETE', url, body }),
};

export default api;
export { notifyAppVersion };
export type { Request };
